import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { yellow, grey } from '@mui/material/colors';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';
import { I18nextProvider } from 'react-i18next';

import { HelmetProvider } from 'react-helmet-async';
import i18n from '#i18n';

import { FeedbackUIProvider } from '#context/feedback-ui-context';
import { bootstrap } from '#bootstrap';
import { enabledAddons } from '#addons';

import App from './App';
import { store } from './store';
import './smallwm.custom.css';

// Initialize Sentry if DSN is configured in environment
const { SENTRY_DSN, NODE_ENV, RELEASE_VERSION } = process.env;

if (SENTRY_DSN) {
  console.log('Initializing Sentry for client');
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Configuration
    tracesSampleRate: NODE_ENV === 'production' ? 0.2 : 1.0,
    environment: NODE_ENV || 'development',
    release: RELEASE_VERSION || '1.0.0',
    maxBreadcrumbs: 50,
    initialScope: {
      tags: { app: 'bwm-client' },
    },

    // Session replay settings
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Enable performance monitoring
    enableTracing: true,
    // Enable capture of errors from the global scope
    autoSessionTracking: true,

    // Safari (isTrusted: false) can dispatch synthetic CustomEvents with type
    // "unhandledrejection" via third-party scripts or the Sentry replay
    // integration. These events have no rejection reason (detail: null) and
    // produce the noise fingerprint
    // "Event `CustomEvent` (type=unhandledrejection) captured as promise
    // rejection". They are not actionable application errors, so we drop them.
    beforeSend(event, hint) {
      const ex = hint?.originalException;
      if (ex instanceof Event && ex.type === 'unhandledrejection' && !ex.isTrusted) {
        return null;
      }
      return event;
    },
  } as Parameters<typeof Sentry.init>[0]);
}

const theme = createTheme({
  palette: {
    // Brand gold, used for the data-table time-range filter accents.
    // Same values as Home.tsx / Header.tsx.
    brand_gold: {
      main: '#d9a258',
      light: '#e8b96e',
      dark: '#b07f3e',
      contrastText: '#fff',
    },
    fulfillment_partial: {
      main: yellow[500],
      contrastText: '#000',
    },
    fulfillment_cancelled: {
      main: grey[200],
      contrastText: '#000',
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

const reactDomEx = ReactDOM as typeof ReactDOM & { __bwm_root?: ReactDOM.Root };

let initialized = false;
function AppWithProvider() {
  if (!initialized) {
    // the root component AppWithProvider
    // will be called multiples caused by unknown reason
    // prevent duplicate initialization for the addons
    initialized = true;
    enabledAddons.forEach((addon) => {
      addon.apply(bootstrap);
    });
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <HelmetProvider>
          <FeedbackUIProvider>
            <BrowserRouter>
              <ThemeProvider theme={theme}>
                <App bootstrap={bootstrap} />
              </ThemeProvider>
            </BrowserRouter>
          </FeedbackUIProvider>
        </HelmetProvider>
      </Provider>
    </I18nextProvider>
  );
}

const rootElement = document.getElementById('root');
if (!reactDomEx.__bwm_root) {
  reactDomEx.__bwm_root = ReactDOM.createRoot(rootElement!);
}

// Wrap with ErrorBoundary only if Sentry is initialized
if (SENTRY_DSN) {
  reactDomEx.__bwm_root.render(
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <AppWithProvider />
    </Sentry.ErrorBoundary>
  );
} else {
  reactDomEx.__bwm_root.render(<AppWithProvider />);
}

// Vite HMR: accept self-updates (replaces webpack's `module.hot`). React
// component refresh is handled by @vitejs/plugin-react; this keeps the entry
// module from forcing a full reload on its own edits. In a production build
// `import.meta.hot` is undefined and this branch is tree-shaken out.
const hot = (import.meta as { hot?: { accept: () => void } }).hot;
if (hot) {
  hot.accept();
}
